/**
 * V3Icon — v3.6 Glass + Neon 라인 아이콘 세트
 * 핸드오프: docs/handoff/video-editor/project/v3/v3-icons.jsx
 * 미니멀 라인, stroke 1.5, viewBox 24x24
 */
import type { CSSProperties, ReactElement, SVGProps } from 'react'

export type V3IconName =
  | 'home' | 'sparkle' | 'wand' | 'film' | 'scissors' | 'folder'
  | 'play' | 'pause'
  | 'arrowR' | 'arrowL' | 'arrowU' | 'arrowD'
  | 'chevD' | 'chevR' | 'chevL'
  | 'plus' | 'x' | 'check'
  | 'search' | 'menu' | 'bell'
  | 'grid' | 'list'
  | 'upload' | 'download' | 'share'
  | 'type' | 'music' | 'mic'
  | 'settings' | 'image' | 'speed' | 'layers' | 'star' | 'trend' | 'bolt'
  | 'clock' | 'eye'
  | 'dots' | 'dotsv'
  | 'crop' | 'captions' | 'record' | 'volume'
  | 'folderPlus' | 'project'

interface V3IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'fill' | 'stroke'> {
  name: V3IconName
  size?: number
  sw?: number
  fill?: string
  stroke?: string
  style?: CSSProperties
}

export function V3Icon({
  name,
  size = 18,
  sw = 1.5,
  fill = 'none',
  stroke = 'currentColor',
  style,
  ...rest
}: V3IconProps) {
  const sharedLine = {
    fill: 'none',
    stroke,
    strokeWidth: sw,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }

  const paths: Record<V3IconName, ReactElement> = {
    home: (
      <path d="M3 11 12 4l9 7M5 9v11h5v-6h4v6h5V9" {...sharedLine} fill={fill} />
    ),
    sparkle: (
      <path
        d="M12 3l1.8 4.5L18 9.3l-4.2 1.8L12 15.6l-1.8-4.5L6 9.3l4.2-1.8L12 3ZM19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2ZM5 14l.7 1.6L7.5 16.5l-1.8.9L5 19l-.7-1.6L2.5 16.5l1.8-.9L5 14Z"
        fill="currentColor"
      />
    ),
    wand: (
      <g {...sharedLine}>
        <path d="m3 21 12-12M14 7l3 3" />
        <path d="m14 4 .5 1.2 1.2.5-1.2.5L14 7l-.5-1.2-1.2-.5 1.2-.5L14 4ZM20 11l.5 1.2 1.2.5-1.2.5L20 14l-.5-1.2L18 12.3l1.2-.5L20 11ZM5 14l.5 1.2 1.2.5-1.2.5L5 17l-.5-1.2-1.2-.5 1.2-.5L5 14Z" />
      </g>
    ),
    film: (
      <g fill="none" stroke={stroke} strokeWidth={sw}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 9h4M3 15h4M17 9h4M17 15h4M9 4v16M15 4v16" />
      </g>
    ),
    scissors: (
      <g {...sharedLine}>
        <circle cx="6" cy="6" r="2.5" />
        <circle cx="6" cy="18" r="2.5" />
        <path d="M20 4 9 15M20 20 12 12" />
      </g>
    ),
    folder: (
      <path
        d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
      />
    ),
    play: <path d="M7 4v16l13-8Z" fill="currentColor" />,
    pause: (
      <g fill="currentColor">
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
      </g>
    ),
    arrowR: <path d="M5 12h14M13 6l6 6-6 6" {...sharedLine} />,
    arrowL: <path d="M19 12H5M11 6l-6 6 6 6" {...sharedLine} />,
    arrowU: <path d="M12 19V5M6 11l6-6 6 6" {...sharedLine} />,
    arrowD: <path d="M12 5v14M6 13l6 6 6-6" {...sharedLine} />,
    chevD: <path d="m4 8 8 8 8-8" {...sharedLine} />,
    chevR: <path d="m9 4 8 8-8 8" {...sharedLine} />,
    chevL: <path d="m15 4-8 8 8 8" {...sharedLine} />,
    plus: <path d="M12 5v14M5 12h14" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />,
    x: <path d="M6 6l12 12M18 6 6 18" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />,
    check: <path d="m4 12 5 5 11-11" {...sharedLine} />,
    search: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <circle cx="11" cy="11" r="6.5" />
        <path d="m20 20-4-4" />
      </g>
    ),
    menu: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <path d="M3 6h18M3 12h18M3 18h18" />
      </g>
    ),
    bell: (
      <path
        d="M6 16V10a6 6 0 0 1 12 0v6l2 2H4l2-2ZM10 20a2 2 0 0 0 4 0"
        {...sharedLine}
      />
    ),
    grid: (
      <g fill="none" stroke={stroke} strokeWidth={sw}>
        <rect x="4" y="4" width="7" height="7" rx="1" />
        <rect x="13" y="4" width="7" height="7" rx="1" />
        <rect x="4" y="13" width="7" height="7" rx="1" />
        <rect x="13" y="13" width="7" height="7" rx="1" />
      </g>
    ),
    list: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <path d="M3 6h18M3 12h18M3 18h18" />
      </g>
    ),
    upload: (
      <g {...sharedLine}>
        <path d="M12 16V4m-5 5 5-5 5 5M4 20h16" />
      </g>
    ),
    download: (
      <g {...sharedLine}>
        <path d="M12 4v12m-5-5 5 5 5-5M4 20h16" />
      </g>
    ),
    share: (
      <g {...sharedLine}>
        <circle cx="6" cy="12" r="2.5" />
        <circle cx="18" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="m8.2 11 7.6-3.5M8.2 13l7.6 3.5" />
      </g>
    ),
    type: <path d="M5 7V5h14v2M9 19h6M12 5v14" {...sharedLine} />,
    music: (
      <g {...sharedLine}>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </g>
    ),
    mic: (
      <g {...sharedLine}>
        <rect x="9" y="3" width="6" height="12" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" />
      </g>
    ),
    settings: (
      <g {...sharedLine}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
      </g>
    ),
    image: (
      <g fill="none" stroke={stroke} strokeWidth={sw}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="11" r="2" />
        <path d="m3 17 6-5 5 4 3-2 4 3" />
      </g>
    ),
    speed: (
      <g {...sharedLine}>
        <path d="M12 4a8 8 0 1 0 8 8" />
        <path d="m12 12 5-3M12 4v0" />
      </g>
    ),
    layers: (
      <g {...sharedLine}>
        <path d="m12 3 9 5-9 5-9-5 9-5ZM3 13l9 5 9-5M3 18l9 5 9-5" />
      </g>
    ),
    star: (
      <path
        d="m12 3 2.6 6.3 6.7.5-5.1 4.4 1.6 6.6L12 17.3l-5.8 3.5 1.6-6.6L2.7 9.8l6.7-.5L12 3Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
      />
    ),
    trend: (
      <g {...sharedLine}>
        <path d="M3 17 9 11l4 4 8-9" />
        <path d="M14 6h7v7" />
      </g>
    ),
    bolt: (
      <path
        d="m13 3-9 12h6l-1 6 9-12h-6l1-6Z"
        fill={fill || 'currentColor'}
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
      />
    ),
    clock: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </g>
    ),
    eye: (
      <g fill="none" stroke={stroke} strokeWidth={sw}>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    ),
    dots: (
      <g fill="currentColor">
        <circle cx="6" cy="12" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="18" cy="12" r="1.6" />
      </g>
    ),
    dotsv: (
      <g fill="currentColor">
        <circle cx="12" cy="6" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="12" cy="18" r="1.6" />
      </g>
    ),
    crop: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <path d="M6 2v16a2 2 0 0 0 2 2h14M2 6h16a2 2 0 0 1 2 2v14" />
      </g>
    ),
    captions: (
      <g {...sharedLine}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 13h2M11 13h6M7 16h4M13 16h4" />
      </g>
    ),
    record: <circle cx="12" cy="12" r="6" fill="currentColor" />,
    volume: (
      <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round">
        <path d="M4 9v6h4l5 4V5L8 9H4Z" fill="currentColor" />
        <path d="M16 8a5 5 0 0 1 0 8M19 5a9 9 0 0 1 0 14" />
      </g>
    ),
    folderPlus: (
      <g {...sharedLine}>
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
        <path d="M12 11v6M9 14h6" />
      </g>
    ),
    project: (
      <g {...sharedLine}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 9h18M8 4v5" />
      </g>
    ),
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'block', flexShrink: 0, ...style }}
      {...rest}
    >
      {paths[name] ?? paths.play}
    </svg>
  )
}
